import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDrop } from "react-dnd";
import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { omit } from "lodash";
import { Ellipsis } from "lucide-react";
import { toast } from "sonner";
import Content from "./Content";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useMenuContext } from "../../../hooks/context/useMenuContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { useHolidays } from "../../../api/useHolidays";
import useApi from "../../../../../../utils/api";
import { useFormatDragAndDropData } from "../../../hooks/useFormatDragAndDropData";
import { getFormFormattedValues } from "../../../helpers/form/getFormFormattedValues";
import { getUnassignedShiftFormattedValues } from "../../../helpers/form/getUnassignedShiftFormattedValues";
import { formatEvents } from "../../../helpers/data/formatEvents";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { combineDateAndTime } from "../../../../../../utils/helpers/date";
import { unassignedShiftsResource } from "../../../helpers/data/unassignedShiftsResource";
import { withLoadingToast } from "../../../helpers/withLoadingToast";
import { getDayUnderSourceStart } from "../../../helpers/grid/getDayUnderSourceStart";
import { getMoreModalPreviewOffset } from "./Content/MoreModal/DragLayer";
import { cn } from "../../../../../../lib/utils";

function CellRender({ index, isCurrentMonth, day, totalDays }: any) {
    const { t } = useTranslation();
    const userTimezone = moment.tz.guess(true);
    const {
        dispatch,
        dragAndDropCopy,
        showCurrentTime,
        calendarEvents,
        unassignedEvents,
        openShifts,
        type,
        dailyInfo
    } = useScheduleContext();
    const { openMenu } = useMenuContext();
    const { authPut, authPost } = useApi();
    const { data: holidays } = useHolidays();
    const isTeamScheduler = type === "team";
    const formatDragAndDropData = useFormatDragAndDropData({});
    const [cellElement, setCellElement] = useState<HTMLDivElement | null>(null);

    const {
        canCreate: canCreateEvent,
        canCopy: canCopyEvent,
        canEdit: canEditEvent,
        canLock: canLockEvent,
        canUnlock: canUnlockEvent,
        canDelete: canDeleteEvent,
        canApprove: canApproveEvent,
        canCreateUnassignedShift,
        canEditUnassignedShift,
    } = usePermissions();

    const { isToday, lastRowStartIndex, isLastRow } = useMemo(() => ({
        isToday: day.isSame(moment(), "day"),
        lastRowStartIndex: totalDays - 7,
        isLastRow: index >= totalDays - 7
    }), [day, index, totalDays]);

    const dayData = useMemo(() => {
        let data: any = calendarEvents?.[moment(day).format("YYYY-MM-DD") as any]?.events || [];
        data = formatEvents(data);
        const dailyInfoEvents = holidays?.flatMap((holiday: any) =>
            holiday.dates.map((date: any) => ({
                ...holiday,
                date,
                type: "daily-info",
            }))
        ).filter((item: any) => moment(item.date).isSame(moment(day), "day")) ?? [];
        const others = [
            ...data.filter((item: any) => item.type !== "unavailable"),
            ...(dailyInfo ? dailyInfoEvents : [])
        ];
        return { data: others, total: calendarEvents?.[moment(day).format("YYYY-MM-DD") as any]?.total || 0 };
    }, [calendarEvents, holidays, day, dailyInfo]);

    const createEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (data: any) => {
            const url = isTeamScheduler ? "/schedules/v2" : "/my/schedules/v2";
            return authPost(url, { data: omit(data, ["id", "oldDate"]) });
        },
        onSuccess: (response: any) => {
            dispatch({
                type: "create-calendar-event",
                payload: {
                    ...response,
                    resourceId: response.user?.id ?? response.user,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                    date: moment.parseZone(response.date).format("YYYY-MM-DD"),
                }
            });
            toast.success(dragAndDropCopy ? t("event-copied-successfully") : t("event-created-successfully"));
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const editEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (data: any) => {
            const url = isTeamScheduler
                ? `/schedules/v2/${data?.id}`
                : `/my/schedules/v2/${data?.id}`;
            const response = await authPut(url, { data: omit(data, ["oldDate"]) });
            return { ...response, oldId: data.id, oldDate: data.oldDate }
        },
        onSuccess: (response: any) => {
            const isSourceUnassigned = response.resourceId === "unassigned-shifts";
            if (isSourceUnassigned) {
                dispatch({ type: "delete-unassigned-event", payload: { id: response.oldId } });
            } else {
                dispatch({
                    type: "delete-calendar-event",
                    payload: { id: response.oldId, date: response?.oldDate }
                });
            }

            if (isSourceUnassigned) {
                dispatch({
                    type: "create-unassigned-event",
                    payload: {
                        ...response,
                        resourceId: "unassigned-shifts",
                        startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                        endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                        date: moment.parseZone(response.date).format("YYYY-MM-DD"),
                    }
                });
                toast.success(t("unassigned-shift-updated-successfully"));
            } else {
                dispatch({
                    type: "create-calendar-event",
                    payload: {
                        ...response,
                        resourceId: response.user?.id ?? response.user,
                        startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                        endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                        date: moment.parseZone(response.date).format("YYYY-MM-DD"),
                    }
                });
                toast.success(t("event-updated-successfully"));
            }
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const createUnassigendShifts = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (values: any) => {
            return authPost(
                "/unassigned-shifts",
                { data: omit(values, ["__v", "_id", "id", "oldDate"]) }
            );
        },
        onSuccess: (response) => {
            dispatch({
                type: "create-unassigned-event",
                payload: {
                    ...response,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                    resourceId: unassignedShiftsResource.id
                }
            });
            toast.success(t("unassigned-shift-updated-successfully"));
        },
        onError: (err: any) => getErrorMessage(err, t)
    });

    const editUnassigendShifts = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (values: any) => {
            return authPut(
                `/unassigned-shifts/${values?.id}`,
                { data: omit(values, ["__v", "_id", "id", "oldDate"]) }
            );
        },
        onSuccess: (response) => {
            dispatch({
                type: "update-unassigned-event",
                payload: {
                    ...response,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                    resourceId: unassignedShiftsResource.id
                }
            });
            toast.success(t("unassigned-shift-updated-successfully"));
        },
        onError: (err: any) => getErrorMessage(err, t)
    });

    const [_, drop] = useDrop({
        accept: "calendar-event",
        drop: (item: any, monitor) => {
            if (monitor.didDrop()) return;
            const isTargetUnassigned = item.resourceId === "unassigned-shifts";
            // Land on the day the event's start is over, not the day under the pointer
            const startOffset = getMoreModalPreviewOffset(item, monitor.getClientOffset()) ?? monitor.getSourceClientOffset();
            const targetDay = getDayUnderSourceStart(moment(day), cellElement, startOffset);

            // Unassigned shifts only move (or copy) to the new date - times, sections and slots stay the same
            if (isTargetUnassigned) {
                const canDropUnassigned = dragAndDropCopy ? canCreateUnassignedShift : canEditUnassignedShift;
                if (!canDropUnassigned) return;
                if (!dragAndDropCopy && moment(item.date).isSame(targetDay, "date")) return;

                const startDate = combineDateAndTime(targetDay, moment(item.startDate));
                const endDate = combineDateAndTime(targetDay, moment(item.endDate));
                if (endDate.isBefore(startDate)) {
                    endDate.add(1, "day");
                }

                const formattedValues = getFormFormattedValues({
                    ...omit(item, ["resourceId", "createdAt", "updatedAt"]),
                    date: targetDay.format("YYYY-MM-DD"),
                    startDate,
                    endDate
                });
                const values = getUnassignedShiftFormattedValues(formattedValues, item.slots);

                if (dragAndDropCopy) {
                    createUnassigendShifts.mutate(values);
                } else {
                    editUnassigendShifts.mutate(values);
                }
                return;
            }

            const newEvent = {
                ...item,
                startDate: combineDateAndTime(targetDay, moment(item.startDate)),
                endDate: combineDateAndTime(targetDay, moment(item.endDate)),
                date: targetDay.format("YYYY-MM-DD"),
                oldDate: item.date
            };

            const data = formatDragAndDropData(newEvent, newEvent.startDate, newEvent?.endDate);
            const formattedValues = getFormFormattedValues(data);

            if (canCopyEvent && dragAndDropCopy) {
                createEventMutation.mutate(formattedValues);
                return;
            }

            if (canEditEvent && !dragAndDropCopy) {
                editEventMutation.mutate(formattedValues);
                return;
            }
        },
    }, [
        day,
        cellElement,
        dragAndDropCopy,
        createUnassigendShifts,
        editUnassigendShifts,
        createEventMutation,
        editEventMutation,
        canCopyEvent,
        canEditEvent,
        canCreateUnassignedShift,
        canEditUnassignedShift,
        formatDragAndDropData,
        dispatch
    ]) as any;

    const onMenuOpen = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        openMenu({
            id: "cell-menu",
            x: e.clientX,
            y: e.clientY,
            selected: day.format("YYYY-MM-DD")
        });
    }, [openMenu, day]);

    const { dailyUnassignedEvents, dailyOpenShifts } = useMemo(() => {
        return {
            dailyUnassignedEvents: unassignedEvents?.filter((event: any) => moment(event?.date).isSame(moment(day), "date")),
            dailyOpenShifts: openShifts?.filter((item: any) => moment(item.data?.schedule?.startDate || item.data?.startDate).isSame(moment(day), "date"))
        }
    }, [unassignedEvents, openShifts, day]);

    return (
        <div
            ref={(node: HTMLDivElement | null) => {
                setCellElement(node);
                drop(node);
            }}
            key={day.format("YYYY-MM-DD")}
            className={cn(
                "tw-p-2 tw-border-r tw-border-b tw-border-neutral-100 tw-relative tw-text-neutral-900 tw-bg-white tw-flex tw-flex-col tw-min-h-0 tw-min-w-0 tw-overflow-hidden",
                !isCurrentMonth && "tw-text-neutral-300",
                (isToday && showCurrentTime) && "tw-bg-primary/5 tw-text-primary tw-font-medium",
                isLastRow && index === lastRowStartIndex && "tw-rounded-bl-md",
                isLastRow && index === totalDays - 1 && "tw-rounded-br-md"
            )}
        >
            <div className="tw-text-sm tw-flex tw-items-center tw-justify-between tw-mb-1 tw-shrink-0">
                {(canCreateEvent || canCreateUnassignedShift || canApproveEvent || canLockEvent || canUnlockEvent || canDeleteEvent) &&
                    (dayData?.total + (dailyUnassignedEvents?.length || 0) + (dailyOpenShifts?.length || 0) +
                        (dailyInfo ? (dayData?.data?.filter((e: any) => e.type === "daily-info")?.length || 0) : 0)) > 0 ? (
                    <div className="tw-cursor-pointer tw-px-1" onClick={onMenuOpen}>
                        <Ellipsis
                            className="tw-text-neutral-700"
                            size={17}
                        />
                    </div>
                ) : <span />}
                <span>
                    {day.date()}
                </span>
            </div>
            <Content day={day} dayData={dayData} />
        </div>

    );
}

export default CellRender;
